import { MenuItem, Menu, Tooltip } from '@mui/material';
import { Check, Delete, Edit, Save } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import usePreferencesMenuStyles from './styles';
import { PreferencesMenuItemProps } from './types';
import { useContextMenu } from 'kineticUtils/hooks';
import { useCallback, useState } from 'react';
import EditableTitle, { useEditableTitle } from 'components/EditableTitle';
import clsx from 'clsx';

const preventNavigation = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const PreferencesMenuItem: React.FunctionComponent<PreferencesMenuItemProps> = (props) => {
  const { t } = useTranslation('common');
  const {
    preference,
    setPreference,
    savePreference,
    updatePreference,
    toggleDefault,
    deletePreference,
    title,
    closeMenu,
    removeCreateMode,
    container,
  } = props;
  const classes = usePreferencesMenuStyles(props);

  const [isEditMode, setIsEditMode] = useState<boolean>(!preference.id);

  const {
    props: actionsProps,
    handleOpenMenu: openActions,
    handleCloseMenu: closeActions,
  } = useContextMenu({ openAtCursor: true });

  const { inputRef, inputValue, onChange, onSave, onCancel, onEdit } = useEditableTitle({
    initialEditMode: false,
    title: preference?.name ?? '',
    onSave: (newTitle) => {
      savePreference(preference.id, newTitle);
      setIsEditMode(false);
      closeMenu();
      removeCreateMode();
    },
    onCancel: () => {
      setIsEditMode(false);
      removeCreateMode();
    },
  });

  const editName = () => {
    if (preference.id) {
      setIsEditMode(true);
      onEdit();
    }
    closeActions();
  };

  const handleDeletePreference = () => {
    preference.id && deletePreference(preference.id);
    closeActions();
  };

  const onContextMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      openActions(e);
    },
    [openActions]
  );

  const setDefault = () => {
    if (!preference.id) {
      return;
    }
    closeMenu();
    setPreference(preference.id);
    toggleDefault(preference.id, true);
    closeActions();
  };

  const removeDefault = () => {
    if (!preference.id) {
      return;
    }
    toggleDefault(preference.id, false);
    closeActions();
  };

  const renderActions = () => {
    return (
      <Menu
        id='PreferenceActions'
        {...actionsProps}
        MenuListProps={{ dense: true }}
        classes={{ list: classes.contextMenu }}
        container={container}
      >
        <MenuItem
          className={classes.subtleIcon}
          onClick={() => {
            if (!preference.id) {
              return;
            }
            updatePreference(preference.id);
            closeActions();
            closeMenu();
          }}
        >
          <Save fontSize='small' className={classes.icon} />

          {t('save')}
        </MenuItem>
        <MenuItem
          className={classes.subtleIcon}
          onClick={preference?.default ? removeDefault : setDefault}
        >
          <Check fontSize='small' className={classes.icon} />
          {preference?.default ? t('removeDefault') : t('setDefault')}
        </MenuItem>
        <MenuItem className={classes.subtleIcon} onClick={editName}>
          <Edit fontSize='small' className={classes.icon} />
          {t('rename')}
        </MenuItem>
        <MenuItem className={classes.subtleIcon} onClick={handleDeletePreference}>
          <Delete fontSize='small' className={classes.icon} />
          {t('delete')}
        </MenuItem>
      </Menu>
    );
  };

  const renderPreferencesMenuItem = () => {
    const { id, name } = preference;
    const onClick = isEditMode
      ? preventNavigation
      : () => {
          if (!id) {
            return;
          }
          setPreference(id);
          closeMenu();
        };
    return (
      <>
        {renderActions()}
        <MenuItem
          href=''
          key={id}
          className={clsx({ [classes.editingMenuItem]: isEditMode }, classes.menuItem)}
          data-id={id}
          onClick={onClick}
          onContextMenu={onContextMenu}
        >
          <EditableTitle
            hideEditIcon
            disableClickToEdit
            inputRef={inputRef}
            inputValue={inputValue}
            onChange={onChange}
            onSave={onSave}
            title={name ?? t('name')}
            onCancel={onCancel}
            onEdit={onEdit}
            isEditMode={isEditMode}
            classes={{
              editableWrapper: classes.editableWrapper,
            }}
          />
          {preference.default && (
            <Tooltip title={t('defaultItem', { title })} className={classes.defaultIcon}>
              <Check color='success' fontSize='small' className={classes.defaultIcon} />
            </Tooltip>
          )}
        </MenuItem>
      </>
    );
  };

  return renderPreferencesMenuItem();
};

export default PreferencesMenuItem;
